// Mapbox helpers shared across the admin panel (geocoding, reverse geocoding,
// place search, directions). All functions take the public access token
// (pk....) explicitly so callers can source it from ConfigurableValues().
// NOTE: admin has no `axios` dependency, so this uses the native `fetch` API.

const GEOCODE_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places'
const DIRECTIONS_BASE = 'https://api.mapbox.com/directions/v5/mapbox/driving'

// ISO 3166-1 alpha-2, comma separated. Scopes every place search to where we
// actually deliver.
const DEFAULT_COUNTRY = 'us'

function buildUrl(base, params) {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&')
  return `${base}?${qs}`
}

// Reverse geocode: coordinates -> { formattedAddress, city }
export async function reverseGeocode(latitude, longitude, token) {
  if (!token) throw new Error('Mapbox token missing')
  const url = buildUrl(`${GEOCODE_BASE}/${longitude},${latitude}.json`, {
    access_token: token,
    language: 'en',
    limit: 1
  })
  const res = await fetch(url)
  const data = await res.json()
  const feature = data?.features?.[0]
  if (!feature) throw new Error('No address found for the given coordinates.')
  const formattedAddress = feature.place_name
  // city = the "place" context entry (locality fallback)
  const cityCtx =
    feature.context?.find(c => c.id.startsWith('place')) ||
    feature.context?.find(c => c.id.startsWith('locality'))
  const city = cityCtx ? cityCtx.text : null
  return { formattedAddress, city }
}

// Forward search: query string -> array of suggestions.
// Each suggestion: { id, name, placeName, lat, lng, bbox }
// Results are limited to the United States by default — we only operate there,
// so worldwide matches are noise. Pass options.country to widen or change it.
// Nearby results come first: pass options.proximity ("lng,lat"), otherwise
// Mapbox biases by the browser's IP. Without either it ranks the whole country
// by text alone, so "15 wright st" listed Indiana and Ohio before Stoneham, MA.
export async function searchPlaces(query, token, options = {}) {
  if (!token || !query) return []
  const url = buildUrl(`${GEOCODE_BASE}/${encodeURIComponent(query)}.json`, {
    access_token: token,
    language: 'en',
    limit: options.limit || 5,
    country: options.country || DEFAULT_COUNTRY,
    proximity: options.proximity || 'ip',
    ...(options.types ? { types: options.types } : {})
  })
  const res = await fetch(url)
  const data = await res.json()
  return (data?.features || []).map(f => ({
    id: f.id,
    name: f.text,
    placeName: f.place_name,
    lng: f.center[0],
    lat: f.center[1],
    bbox: f.bbox || null // [minLng, minLat, maxLng, maxLat]
  }))
}

// Driving directions between two {lat,lng} points.
// Returns { geojson, distance, duration } where geojson is a LineString Feature.
export async function getDirections(origin, dest, token) {
  if (!token || !origin || !dest) return null
  const coords = `${origin.lng},${origin.lat};${dest.lng},${dest.lat}`
  const url = buildUrl(`${DIRECTIONS_BASE}/${coords}`, {
    access_token: token,
    geometries: 'geojson',
    overview: 'full'
  })
  const res = await fetch(url)
  const data = await res.json()
  const route = data?.routes?.[0]
  if (!route) return null
  return {
    geojson: { type: 'Feature', properties: {}, geometry: route.geometry },
    distance: route.distance,
    duration: route.duration
  }
}
